import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Seo from "../../components/feature/Seo";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/da23r9jk8r5te5mqsfrg";

const channels = [
  { icon: "ri-mail-line", label: "邮箱", value: "contact@cechimanga.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "策驰漫画网" },
  { icon: "ri-bilibili-line", label: "哔哩哔哩", value: "@策驰漫画" },
  { icon: "ri-time-line", label: "反馈时效", value: "通常1-3个工作日内回复" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾蜜罐检测
    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    // 前端校验
    const name = (formData.get("name") as string) || "";
    const email = (formData.get("email") as string) || "";
    const content = (formData.get("content") as string) || "";
    if (!name.trim() || !email.trim() || !content.trim()) {
      setStatus("error");
      setFormError("请填写姓名、邮箱和留言内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus("error");
      setFormError("请输入有效的邮箱地址。");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过500个字符。");
      return;
    }

    // 构建提交数据（移除蜜罐字段）
    const payload = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (key === "phone_alt") continue;
      payload.append(key, value.toString());
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");
    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam|form data is spam/i.test(serverMsg || "");
      const isOk =
        response.ok &&
        parsed?.code === "OK" &&
        !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <Seo
        title="联系我们 - 策驰漫画网"
        description="联系策驰漫画网，提供意见反馈、作品推荐与合作洽谈渠道。填写留言表单，我们会尽快与你取得联系。"
        canonical="https://artbinge.github.io/lianxi"
      />

      <section className="pt-16 md:pt-[72px] bg-primary-600 relative overflow-hidden">
        <div className="absolute -bottom-10 -left-8 w-56 h-56 rounded-full bg-accent-500/30 blur-2xl"></div>
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 md:px-6 py-10 md:py-12">
          <nav aria-label="面包屑" className="text-primary-100 text-xs md:text-sm mb-3">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">联系我们</li>
            </ol>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl text-background-50">联系我们</h1>
          <p className="mt-2 text-primary-100 text-sm md:text-base">意见反馈、作品推荐与合作洽谈，欢迎与我们联系</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 联系渠道 */}
          <div>
            <h2 className="font-heading text-2xl text-primary-700 mb-6">联系渠道</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {channels.map((ch) => (
                <div key={ch.label} className="bg-background-100 border border-background-200/70 rounded-2xl p-5">
                  <span className="w-10 h-10 rounded-xl bg-secondary-100 text-secondary-900 flex items-center justify-center text-lg"><i className={ch.icon}></i></span>
                  <p className="mt-3 text-xs text-foreground-500">{ch.label}</p>
                  <p className="mt-1 text-sm font-semibold text-foreground-900 break-all">{ch.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
              <h3 className="flex items-center gap-2 font-bold text-foreground-950"><i className="ri-question-line text-accent-600"></i>常见问题</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                关于漫画书库、人气排行、编辑精选与完结推荐等使用问题，可以先去首页底部的「常见问题」板块看看，大部分疑问都能在那里找到答案。
              </p>
              <Link to="/#faq" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-700 hover:text-accent-800">查看常见问题 <i className="ri-arrow-right-line"></i></Link>
            </div>
          </div>

          {/* 留言表单 */}
          <div className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-2xl text-primary-700 mb-2">在线留言</h2>
            <p className="text-sm text-foreground-600 mb-6">填写下方表单，我们会尽快与你取得联系。</p>

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">你的称呼 <span className="text-primary-600">*</span></label>
                <input id="name" name="name" type="text" maxLength={30} placeholder="怎么称呼你" className="w-full h-11 px-4 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">联系邮箱 <span className="text-primary-600">*</span></label>
                <input id="email" name="email" type="email" placeholder="name@example.com" className="w-full h-11 px-4 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition" />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">留言主题</label>
                <select id="subject" name="subject" className="w-full h-11 px-4 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition">
                  <option value="意见反馈">意见反馈</option>
                  <option value="作品推荐">作品推荐</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容 <span className="text-primary-600">*</span></label>
                <textarea id="content" name="content" rows={5} maxLength={500} placeholder="请描述你的问题或建议（500字以内）" className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition resize-none"></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多500字</p>
              </div>

              {/* 反垃圾蜜罐字段 */}
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="phone_alt">请不要填写此栏</label>
                <input id="phone_alt" name="phone_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              {status === "success" && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-100 text-secondary-900 text-sm"><i className="ri-checkbox-circle-fill mt-0.5"></i>提交成功，感谢你的留言，我们会尽快回复。</div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-primary-100 text-primary-800 text-sm"><i className="ri-error-warning-fill mt-0.5"></i>{formError}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full whitespace-nowrap h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                {submitting ? "提交中..." : "提交留言"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* ContactPage 结构化数据 */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "联系我们 - 策驰漫画网",
        "description": "策驰漫画网联系页面，提供意见反馈、作品推荐与合作洽谈渠道。",
        "inLanguage": "zh-CN",
      }) }} />
    </div>
  );
}